import HorizontalDivider from '@/global/components/Divider/HorizontalDivider'
import Grid from '@/global/components/ui/Grid'
import { CidadesEstado, estadosV2, getCidadesByUf } from '@acme/common'
import { FormFieldAutocomplete, FormFieldSelect } from 'global/components/Form'
import { useEffect, useLayoutEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { Api } from 'service/api'

interface CidadeTrabalho {
    uf: string
    cidade: string
    ibge: number
}

interface IProps {
    noDivider?: boolean
    className?: string
}

const OPCOES_UF = estadosV2.map(({ sigla, nome }) => ({
    value: sigla,
    label: nome,
}))

function UFFuncionario({
    cidadeTrabalho,
}: {
    cidadeTrabalho: CidadeTrabalho | null
}) {
    return (
        <FormFieldSelect
            required
            name="uf"
            label="Estado"
            defaultValue={cidadeTrabalho?.uf}
            options={OPCOES_UF}
        />
    )
}

function CidadeFuncionario({
    cidades,
    cidadeTrabalho,
}: {
    cidades: CidadesEstado[]
    cidadeTrabalho: CidadeTrabalho | null
}) {
    return (
        <FormFieldAutocomplete
            name="Cidade"
            label="Cidade"
            options={cidades.map(cidade => ({
                cidade: cidade.nome,
                uf: cidade.uf,
                ibge: cidade.id,
            }))}
            getOptionLabel={option => option.cidade}
            // O código do IBGE é o que identifica a cidade: a opção da lista e
            // o valor guardado no formulário são objetos diferentes, então
            // comparar por referência nunca marcaria a cidade escolhida.
            getOptionKey={option => String(option.ibge)}
            isOptionEqualToValue={(option, value) =>
                option?.ibge === value?.ibge
            }
            // Sem as cidades do estado carregadas não há opção que corresponda
            // ao padrão, então ele só entra quando a lista já chegou.
            defaultValue={
                cidades.length > 0 ? (cidadeTrabalho ?? undefined) : undefined
            }
            required
        />
    )
}

export function Endereco({ noDivider, className }: IProps) {
    const { watch, setValue } = useFormContext()
    const [cidades, setCidades] = useState<CidadesEstado[]>([])
    const [cidadeTrabalho, setCidadeTrabalho] = useState<CidadeTrabalho | null>(
        null,
    )
    const uf = watch('uf')

    async function getCidadeTrabalho(id: number) {
        const res = await Api.cidade.getById(id)

        setValue('uf', res.uf)
        setValue('Cidade', {
            uf: res.uf,
            cidade: res.cidade,
            ibge: res.ibge,
        })

        const cidades = await getCidadesByUf(res.uf)
        setCidades(cidades)
        setCidadeTrabalho({
            uf: res.uf,
            cidade: res.cidade,
            ibge: res.ibge,
        })
    }

    useEffect(() => {
        const cidadeTrabalho = watch('cidadeTrabalho')
        if (cidadeTrabalho) {
            getCidadeTrabalho(cidadeTrabalho)
        }
    }, [])

    useLayoutEffect(() => {
        ;(async () => {
            if (uf !== undefined) {
                const cidades = await getCidadesByUf(uf)
                setCidades(cidades)
            }
        })()
    }, [setValue, uf])

    return (
        <section id="endereço" className={className}>
            {!noDivider && (
                <HorizontalDivider className="my-4">
                    Cidade de trabalho
                </HorizontalDivider>
            )}
            <Grid container columns={24}>
                <Grid item xs={10}>
                    <UFFuncionario cidadeTrabalho={cidadeTrabalho} />
                </Grid>
                <Grid item xs={14}>
                    <CidadeFuncionario
                        cidades={cidades}
                        cidadeTrabalho={cidadeTrabalho}
                    />
                </Grid>
            </Grid>
        </section>
    )
}
